import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
  IconographyThemes,
  Button as HIDButton,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import "../Modals.scss";
import { useAppSelector } from "../../../../../../redux";

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editData?: any;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  open,
  onClose,
  onSave,
  editData,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    categoryName: "",
    description: "",
  });
  const { status } = useAppSelector((state) => state.project_validation);

  const isSubmitting = editData
    ? status.updateCategoryLoading
    : status.createCategoryLoading;
  // Update form when editData changes
  React.useEffect(() => {
    if (editData) {
      setFormData({
        categoryName: editData.category_name || "",
        description: editData.description || "",
      });
    } else {
      setFormData({ categoryName: "", description: "" });
    }
    setSubmitted(false);
    setErrors({});
  }, [editData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    setFormData({
      ...formData,
      [name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.categoryName) {
      newErrors.categoryName = "Category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm() && !isSubmitting) {
      setSubmitted(true);
      onSave(formData);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitted(false);
      setErrors({});
      setFormData({ categoryName: "", description: "" });
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      className="category-modal"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {submitted ? (
        <div style={{ padding: "40px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
          {isSubmitting ? (
            <>
              <Loader variant={LoaderSizes.large} />
              <Typography
                variant="body2"
                sx={{ color: "#374151", textAlign: "center" }}
              >
                {editData ? "Updating category..." : "Creating category..."}
              </Typography>
            </>
          ) : (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <Typography
                variant="body2"
                sx={{ color: "#374151", textAlign: "center" }}
              >
                {editData
                  ? "Category updated successfully"
                  : "Category created successfully"}
              </Typography>
              <HIDButton label="Close" onClick={handleClose} variant="primary" />
            </>
          )}
        </div>
      ) : (
        <>
          <DialogTitle
            sx={{
              pb: 0,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ pr: 1 }}>
              <Typography
                sx={{
                  fontFamily: "'Roboto'",
                  fontWeight: 400,
                  fontSize: "24px",
                  lineHeight: "30px",
                  color: "#222222",
                  mt: 1.5,
                }}
              >
                {editData ? "Update Category" : "Create Category"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Roboto'",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#737373",
                  mb: 1.5,
                }}
              >
                Configure a new Category type
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{ mt: 0.5 }}
            >
              <Iconography icon="xmark" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: "'Roboto'",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#374151",
                  mb: 0.5,
                }}
              >
                Category Name *
              </Typography>
              <TextField
                fullWidth
                name="categoryName"
                value={formData.categoryName}
                onChange={handleChange}
                placeholder="XYZ"
                variant="outlined"
                size="small"
                error={!!errors.categoryName}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F7F7F7",
                    borderRadius: "8px",
                  },
                }}
              />
              {errors.categoryName && (
                <Typography
                  sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: 0.5 }}
                >
                  {errors.categoryName}
                </Typography>
              )}
            </Box>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Roboto'",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#374151",
                  }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Roboto'",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "#9ca3af",
                  }}
                >
                  {formData.description.length}/500
                </Typography>
              </Box>
              <TextField
                fullWidth
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="Enter description"
                variant="outlined"
                inputProps={{ maxLength: 500 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F7F7F7",
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <HIDButton
              onClick={handleClose}
              label="Cancel"
              variant="secondary"
              disabled={isSubmitting}
            />
            <HIDButton
              onClick={handleSubmit}
              label={editData ? "UPDATE CATEGORY" : "CREATE CATEGORY"}
              variant="primary"
              disabled={isSubmitting}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AddCategoryModal;
